import { useState, useEffect } from 'react';
import type { Insight, InsightCategory, SourceType } from '@claude-code-insights/shared';
import { INSIGHT_CATEGORIES, SOURCE_TYPES } from '@claude-code-insights/shared';
import { fetchInsights, updateInsight } from '../lib/api';
import { InsightCard } from '../components/InsightCard';

type SortBy = 'createdAt' | 'relevanceScore' | 'title';
type SortDirection = 'asc' | 'desc';

export function FeedView() {
  const [insights, setInsights] = useState<Insight[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);

  // Filters
  const [category, setCategory] = useState<InsightCategory | ''>('');
  const [sourceType, setSourceType] = useState<SourceType | ''>('');
  const [minRelevance, setMinRelevance] = useState<number>(0);
  const [bookmarked, setBookmarked] = useState(false);

  // Sort
  const [sortBy, setSortBy] = useState<SortBy>('createdAt');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  async function loadInsights(append = false) {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchInsights({
        category: category || undefined,
        sourceType: sourceType || undefined,
        minRelevance: minRelevance || undefined,
        bookmarked: bookmarked || undefined,
        sortBy,
        sortDirection,
        limit: 20,
        cursor: append ? cursor ?? undefined : undefined,
      });
      setInsights((prev) => (append ? [...prev, ...result.items] : result.items));
      setCursor(result.nextCursor);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadInsights();
  }, [category, sourceType, minRelevance, bookmarked, sortBy, sortDirection]);

  async function handleToggleBookmark(insightId: string, newBookmarked: boolean) {
    try {
      const updated = await updateInsight(insightId, { bookmarked: newBookmarked });
      setInsights((prev) =>
        prev.map((i) => (i.insightId === insightId ? { ...i, ...updated } : i)),
      );
    } catch (err) {
      console.error('Failed to toggle bookmark:', err);
    }
  }

  return (
    <div className="p-3 md:p-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-4 md:mb-6">
        <h2 className="text-lg font-semibold">Recent Insights</h2>
      </div>

      {/* Filter bar */}
      <div className="flex gap-2 md:gap-3 mb-4 flex-wrap">
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value as InsightCategory | '')}
          className="input text-sm flex-1 min-w-[120px] md:flex-none md:min-w-0"
        >
          <option value="">All categories</option>
          {INSIGHT_CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>

        <select
          value={sourceType}
          onChange={(e) => setSourceType(e.target.value as SourceType | '')}
          className="input text-sm flex-1 min-w-[120px] md:flex-none md:min-w-0"
        >
          <option value="">All sources</option>
          {SOURCE_TYPES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>

        <select
          value={minRelevance}
          onChange={(e) => setMinRelevance(Number(e.target.value))}
          className="input text-sm flex-1 min-w-[120px] md:flex-none md:min-w-0"
        >
          <option value={0}>Any relevance</option>
          <option value={5}>5+ relevance</option>
          <option value={7}>7+ relevance</option>
          <option value={9}>9+ relevance</option>
        </select>

        <button
          onClick={() => setBookmarked(!bookmarked)}
          className={`input text-sm flex items-center gap-1.5 cursor-pointer ${
            bookmarked ? 'border-obsidian-warning text-obsidian-warning' : ''
          }`}
        >
          {bookmarked ? '\u2605' : '\u2606'} Bookmarked
        </button>
      </div>

      {/* Sort bar */}
      <div className="flex gap-2 md:gap-3 mb-4 md:mb-6 items-center flex-wrap">
        <span className="text-xs text-obsidian-text-faint">Sort by</span>
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value as SortBy)}
          className="input text-sm"
        >
          <option value="createdAt">Date</option>
          <option value="relevanceScore">Relevance</option>
          <option value="title">Title</option>
        </select>

        <button
          onClick={() => setSortDirection(sortDirection === 'desc' ? 'asc' : 'desc')}
          className="input text-sm cursor-pointer flex items-center gap-1"
          title={sortDirection === 'desc' ? 'Descending' : 'Ascending'}
        >
          {sortDirection === 'desc' ? '\u2193' : '\u2191'}
          {sortDirection === 'desc' ? 'Desc' : 'Asc'}
        </button>
      </div>

      {/* Error state */}
      {error && (
        <div className="mb-4 p-3 bg-obsidian-error/10 border border-obsidian-error/20 rounded-lg text-sm text-obsidian-error">
          {error}
        </div>
      )}

      {/* Insights list */}
      <div className="space-y-3">
        {insights.map((insight) => (
          <InsightCard
            key={insight.insightId}
            insight={insight}
            onToggleBookmark={handleToggleBookmark}
          />
        ))}
      </div>

      {/* Loading state */}
      {loading && (
        <div className="py-8 text-center text-obsidian-text-muted">
          Loading...
        </div>
      )}

      {/* Empty state */}
      {!loading && insights.length === 0 && (
        <div className="py-16 text-center">
          <p className="text-obsidian-text-muted text-sm">No insights yet</p>
          <p className="text-obsidian-text-faint text-xs mt-2">
            Send a link to your Telegram bot to get started
          </p>
        </div>
      )}

      {/* Load more */}
      {cursor && !loading && (
        <div className="mt-6 text-center">
          <button
            onClick={() => loadInsights(true)}
            className="btn-ghost text-sm"
          >
            Load more
          </button>
        </div>
      )}
    </div>
  );
}
